<template>
  <div class="user-set main-panel large">
    <h1 class="panel-title">公司简介</h1>

    <div class="top">
      <!-- PC / 手机端 切换按钮 -->
      <el-radio-group
        v-model="deviceType"
        class="type-switch"
        fill="#ff9900"
        text-color="#fff"
      >
        <el-radio-button :label="1">PC端</el-radio-button>
        <el-radio-button :label="2">手机端</el-radio-button>
      </el-radio-group>

      <div class="form">
        <div class="row">
          <div class="item col-1">
            <div
              class="label"
              style="font-size: 18px; width: 50px; margin-left: 20px"
            >
              上传：
            </div>
            <el-upload
              style="margin-left: 12px"
              :show-file-list="false"
              accept="image/*"
              :auto-upload="false"
              multiple
              :on-change="handleFileChange"
            >
              <el-button
                class="publish-btn"
                size="big"
                :disabled="currentList.length >= MAX_COUNT"
                ><span style="font-size: 25px">+</span></el-button
              >
            </el-upload>
            <div
              class="label"
              style="
                margin-left: 20px;
                font-size: 16px;
                font-weight: 700;
                color: black;
              "
            >
              {{
                deviceType === 1 ? "PC端图片" : "手机端图片"
              }}（最多10张，拖拽排序）
            </div>
          </div>
        </div>
      </div>
      <div class="form-options">
        <el-button class="publish-btn" size="big" @click="submitUpload">
          保存
        </el-button>
      </div>
    </div>

    <!-- 已选图片列表（拖拽排序） -->
    <div class="img-list" v-if="currentList.length">
      <div
        class="img-item"
        v-for="(item, index) in currentList"
        :key="item.url"
        :class="{ dragging: dragIndex === index }"
        draggable="true"
        @dragstart="onDragStart(index)"
        @dragover.prevent
        @drop="onDrop(index)"
        @dragend="dragIndex = -1"
      >
        <img :src="item.url" class="thumb" />
        <div class="img-actions">
          <el-button
            size="small"
            type="danger"
            circle
            icon="el-icon-delete"
            @click="removeImg(item, index)"
          />
        </div>
        <div class="img-sort"><i class="el-icon-rank"></i> {{ index + 1 }}</div>
      </div>
    </div>
    <el-empty v-else description="暂无图片，请先上传" :image-size="80" />

    <!-- 预览区 -->
    <div class="preview-area">
      <!-- PC 端预览：铺满效果 -->
      <div class="pc-preview" v-if="deviceType === 1">
        <div class="pc-preview-body">
          <template v-if="pcList.length">
            <img
              v-for="item in pcList"
              :key="item.url"
              :src="item.url"
              class="pc-img"
            />
          </template>
          <div v-else class="preview-empty">PC端预览（图片将在此铺满展示）</div>
        </div>
      </div>

      <!-- 手机端预览 -->
      <div class="mobile-preview" v-else>
        <div class="phone-frame">
          <div class="phone-body">
            <template v-if="mobileList.length">
              <img
                v-for="item in mobileList"
                :key="item.url"
                :src="item.url"
                class="mobile-img"
              />
            </template>
            <div v-else class="preview-empty">手机端预览</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted } from "vue";
const { proxy } = getCurrentInstance();

// 1=PC  2=手机
const deviceType = ref(1);
const MAX_COUNT = 10; // 每端最多10张
const pcList = ref([]); // [{ id, file, url, type, delflag, isNew }]
const mobileList = ref([]);
// 从服务端加载后被用户删除的旧图，保存时以 delflag=0 提交给后端
const removedList = ref([]);

const currentList = computed(() =>
  deviceType.value === 1 ? pcList.value : mobileList.value
);

// ---------- 上传 ----------
let overLimitTipLock = false;
const handleFileChange = (file) => {
  // 超出10张限制（多选时逐个校验，只提示一次）
  if (currentList.value.length >= MAX_COUNT) {
    if (!overLimitTipLock) {
      overLimitTipLock = true;
      proxy.$message.warning(`每端最多上传${MAX_COUNT}张图片`);
      setTimeout(() => (overLimitTipLock = false), 1500);
    }
    return;
  }
  currentList.value.push({
    id: "",
    file: file.raw, // File 对象，提交时用
    url: URL.createObjectURL(file.raw), // 本地预览地址
    type: deviceType.value,
    delflag: 1,
    isNew: true,
  });
};

// ---------- 拖拽排序 ----------
const dragIndex = ref(-1);

const onDragStart = (index) => {
  dragIndex.value = index;
};

// 拖到目标位置：把被拖的项插到目标位置（同组内排序）
const onDrop = (index) => {
  if (dragIndex.value === -1 || dragIndex.value === index) return;
  const list = currentList.value;
  const [moved] = list.splice(dragIndex.value, 1);
  list.splice(index, 0, moved);
  dragIndex.value = -1;
};

// 删除图片（旧图记入 removedList，保存时标记 delflag=0）
const removeImg = (item, index) => {
  if (!item.isNew && item.id) {
    removedList.value.push({ ...item, delflag: 0 });
  }
  currentList.value.splice(index, 1);
};

// 保存：按后端要求的 profileList[i].xxx 格式组装 FormData
const submitUpload = async () => {
  const all = [...pcList.value, ...mobileList.value, ...removedList.value];
  if (!all.length) {
    proxy.$message.warning("请先上传图片");
    return;
  }
  const fd = new FormData();
  all.forEach((item, i) => {
    fd.append(`profileList[${i}].id`, item.id || ""); // 修改时传id，新增传空
    if (item.file) fd.append(`profileList[${i}].file`, item.file); // 已删除的旧图没有file
    fd.append(`profileList[${i}].type`, String(item.type)); // 1pc 2手机
    fd.append(`profileList[${i}].sort`, String(i + 1)); // 按当前数组顺序即拖拽后的顺序
    fd.append(`profileList[${i}].delflag`, String(item.delflag ?? 1)); // 1未删除 0已删除
  });
  console.log(all, "看看参数是否正确");
  const res = await proxy.$api.Operation.Yyyhaoprofile.save(fd);
  if (res.data.code == 200) {
    proxy.$message.success("上传成功");
    removedList.value = [];
    getList();
  } else {
    proxy.$message.error("上传失败");
  }
};

// 编辑回显：查询公司简介，返回 data.pc / data.phone 两组，分别回显
const getList = async () => {
  const res = await proxy.$api.Operation.Yyyhaoprofile.get();
  if (res.data.code == 200) {
    const data = res.data.data || {};
    const mapItem = (item) => ({
      id: item.id,
      file: null, // 已有图片没有本地File，不重复传file（按后端约定）
      url: item.url.startsWith("https") ? item.url : "https://" + item.url,
      type: Number(item.type),
      delflag: item.delflag,
      sort: item.sort,
      isNew: false,
    });
    // 按 sort 升序展示
    pcList.value = (data.pc || []).sort((a, b) => a.sort - b.sort).map(mapItem);
    mobileList.value = (data.phone || [])
      .sort((a, b) => a.sort - b.sort)
      .map(mapItem);
  }
};

onMounted(() => {
  getList();
});
</script>

<style lang="scss" scoped>
@import "./yyyhao-profile.scss";

.panel-title {
  color: #999;
}

.type-switch {
  margin-bottom: 16px;
}

.img-list {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 16px 0;

  .img-item {
    width: 120px;
    text-align: center;
    cursor: grab;
    border-radius: 6px;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    }

    &.dragging {
      opacity: 0.5;
      cursor: grabbing;
    }

    .thumb {
      width: 120px;
      height: 80px;
      object-fit: cover;
      border-radius: 4px;
      border: 1px solid #eee;
      display: block;
      pointer-events: none; /* 避免img默认拖拽行为干扰 */
    }

    .img-actions {
      margin-top: 6px;
      display: flex;
      justify-content: center;
      gap: 4px;

      .el-button + .el-button {
        margin-left: 0;
      }
    }

    .img-sort {
      margin-top: 4px;
      font-size: 12px;
      color: #999;
    }
  }
}

/* ---------- 预览区 ---------- */
.preview-area {
  margin-top: 24px;
}

.preview-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
  font-size: 14px;
}

/* PC 端预览：模拟浏览器窗口，图片铺满宽度 */
.pc-preview {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;

  .pc-preview-body {
    height: 480px;
    overflow-y: auto;
    background: #fafafa;

    .pc-img {
      display: block;
      width: 100%; /* 铺满宽度 */
      height: auto; /* 高度按原图比例自适应，完整显示 */
    }
  }
}

/* 手机端预览：模拟手机屏幕 */
.mobile-preview {
  display: flex;
  justify-content: center;

  .phone-frame {
    width: 375px;
    height: 667px;
    border: 2px solid #222;
    border-radius: 36px;
    overflow: hidden;
    background: #fff;
    position: relative;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

    .phone-body {
      height: 100%;
      overflow-y: auto;
      background: #fafafa;

      .mobile-img {
        display: block;
        width: 100%;
        height: auto; /* 按原图比例完整显示 */
      }
    }
  }
}

.publish-btn {
  background-color: #ff9900;
}
</style>
